import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';

interface UnmatchedItem {
  value: string;
  occurrences: { certificateCount: number; roleReqCount: number; vacancyCount: number; workerRoleCount: number };
  suggestions: { canonical: string }[];
}
interface Report {
  unmatchedCertStrings: UnmatchedItem[];
  unmatchedRoleStrings: UnmatchedItem[];
}

export function ReferenceHealthCard() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'reference-health'],
    queryFn: () => api.get<Report>('/settings/reference-health').then((r) => r.data),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['settings', 'reference-health'] });
    qc.invalidateQueries({ queryKey: ['settings', 'cert-types'] });
    qc.invalidateQueries({ queryKey: ['settings', 'roles'] });
  };

  const snap = useMutation({
    mutationFn: (body: { from: string; to: string; kind: 'cert' | 'role' }) =>
      api.post('/settings/reference-health/snap', body).then((r) => r.data),
    onSuccess: (res, vars) => { toast.success(`Snapped "${vars.from}" → "${vars.to}"`, summariseSnap(res)); invalidate(); },
    onError: (e: any) => toast.error('Snap failed', e?.response?.data?.message ?? 'Try again'),
  });
  const promote = useMutation({
    mutationFn: (body: { value: string; kind: 'cert' | 'role' }) =>
      api.post('/settings/reference-health/promote', body).then((r) => r.data),
    onSuccess: (_, vars) => { toast.success(`Promoted "${vars.value}" to canonical`); invalidate(); },
    onError: (e: any) => toast.error('Promote failed', e?.response?.data?.message ?? 'Try again'),
  });
  const ignore = useMutation({
    mutationFn: (body: { value: string; kind: 'cert' | 'role' }) =>
      api.post('/settings/reference-health/ignore', body).then((r) => r.data),
    onSuccess: (_, vars) => { toast.success(`Ignored "${vars.value}"`); invalidate(); },
    onError: (e: any) => toast.error('Ignore failed'),
  });

  const certTotal = data?.unmatchedCertStrings.length ?? 0;
  const roleTotal = data?.unmatchedRoleStrings.length ?? 0;
  const total = certTotal + roleTotal;

  return (
    <div className="card" style={{ gridColumn: '1 / -1' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div className="sec-title" style={{ margin: 0 }}>
          <span style={{ color: total === 0 ? 'var(--live)' : 'var(--warn)' }}>{total === 0 ? '✓' : '⚠'}</span>{' '}
          Reference Health
          {total > 0 ? <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--muted)', fontWeight: 400 }}>{total} unmatched string{total === 1 ? '' : 's'}</span> : null}
        </div>
        <span style={{ fontSize: 10, color: 'var(--muted)' }}>Cert &amp; role names referenced anywhere that don&apos;t match the canonical Settings list</span>
      </div>
      {isLoading ? (
        <div style={{ fontSize: 11, color: 'var(--muted)', padding: 8 }}>Loading…</div>
      ) : total === 0 ? (
        <div style={{ fontSize: 11, color: 'var(--muted)', padding: 12, background: 'rgba(34,197,94,.06)', border: '1px solid rgba(34,197,94,.25)', borderRadius: 6 }}>
          ✓ All cert and role references match the canonical lists. No cleanup needed.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {certTotal > 0 ? (
            <Section
              title={`Cert names (${certTotal})`}
              kind="cert"
              items={data!.unmatchedCertStrings}
              onSnap={(from, to) => snap.mutate({ from, to, kind: 'cert' })}
              onPromote={(value) => promote.mutate({ value, kind: 'cert' })}
              onIgnore={(value) => ignore.mutate({ value, kind: 'cert' })}
              busy={snap.isPending || promote.isPending || ignore.isPending}
            />
          ) : null}
          {roleTotal > 0 ? (
            <Section
              title={`Role names (${roleTotal})`}
              kind="role"
              items={data!.unmatchedRoleStrings}
              onSnap={(from, to) => snap.mutate({ from, to, kind: 'role' })}
              onPromote={(value) => promote.mutate({ value, kind: 'role' })}
              onIgnore={(value) => ignore.mutate({ value, kind: 'role' })}
              busy={snap.isPending || promote.isPending || ignore.isPending}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

function Section({ title, kind, items, onSnap, onPromote, onIgnore, busy }: {
  title: string;
  kind: 'cert' | 'role';
  items: UnmatchedItem[];
  onSnap: (from: string, to: string) => void;
  onPromote: (value: string) => void;
  onIgnore: (value: string) => void;
  busy: boolean;
}) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>{title}</div>
      <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', fontSize: 11 }}>
          <thead>
            <tr style={{ background: 'var(--surface)' }}>
              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Value</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', width: 80 }}>Used in</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', width: 360 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <Row key={item.value} item={item} kind={kind} onSnap={onSnap} onPromote={onPromote} onIgnore={onIgnore} busy={busy} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Row({ item, kind, onSnap, onPromote, onIgnore, busy }: {
  item: UnmatchedItem;
  kind: 'cert' | 'role';
  onSnap: (from: string, to: string) => void;
  onPromote: (value: string) => void;
  onIgnore: (value: string) => void;
  busy: boolean;
}) {
  const [snapTo, setSnapTo] = useState<string>(item.suggestions[0]?.canonical ?? '');
  const occ = item.occurrences;
  const total = occ.certificateCount + occ.roleReqCount + occ.vacancyCount + occ.workerRoleCount;
  const wheres: string[] = [];
  if (occ.certificateCount) wheres.push(`${occ.certificateCount} worker cert${occ.certificateCount === 1 ? '' : 's'}`);
  if (occ.roleReqCount) wheres.push(`${occ.roleReqCount} role req${occ.roleReqCount === 1 ? '' : 's'}`);
  if (occ.vacancyCount) wheres.push(`${occ.vacancyCount} vacanc${occ.vacancyCount === 1 ? 'y' : 'ies'}`);
  if (occ.workerRoleCount) wheres.push(`${occ.workerRoleCount} worker role${occ.workerRoleCount === 1 ? '' : 's'}`);

  return (
    <tr style={{ borderTop: '1px solid var(--border)' }}>
      <td style={{ padding: '8px', fontWeight: 600 }}>
        {item.value}
        <div style={{ fontSize: 9, color: 'var(--muted)', marginTop: 2, fontWeight: 400 }}>{wheres.join(' · ')}</div>
      </td>
      <td style={{ padding: '8px', textAlign: 'right', color: 'var(--muted)' }}>{total}</td>
      <td style={{ padding: '8px' }}>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
          {item.suggestions.length > 0 ? (
            <>
              <span style={{ fontSize: 10, color: 'var(--muted)' }}>Snap to:</span>
              <select
                className="form-input"
                style={{ fontSize: 11, padding: '3px 6px', minWidth: 140 }}
                value={snapTo}
                onChange={(e) => setSnapTo(e.target.value)}
                disabled={busy}
              >
                {item.suggestions.map((s) => (
                  <option key={s.canonical} value={s.canonical}>{s.canonical}</option>
                ))}
              </select>
              <button
                className="btn btn-primary btn-xs"
                disabled={busy || !snapTo}
                onClick={() => onSnap(item.value, snapTo)}
              >Snap</button>
            </>
          ) : (
            <span style={{ fontSize: 10, color: 'var(--muted)', fontStyle: 'italic' }}>No close matches</span>
          )}
          <button
            className="btn btn-ghost btn-xs"
            disabled={busy}
            onClick={() => { if (confirm(`Promote "${item.value}" as a new canonical ${kind === 'cert' ? 'CertType' : 'Role'}?`)) onPromote(item.value); }}
            title="Add this string as a new canonical reference value"
          >+ Promote</button>
          <button
            className="btn btn-ghost btn-xs"
            disabled={busy}
            onClick={() => onIgnore(item.value)}
            title="Hide from this report (data left as-is)"
          >Ignore</button>
        </div>
      </td>
    </tr>
  );
}

function summariseSnap(res: any): string {
  const parts: string[] = [];
  if (res?.certificatesUpdated) parts.push(`${res.certificatesUpdated} cert${res.certificatesUpdated === 1 ? '' : 's'}`);
  if (res?.roleRequirementsUpdated) parts.push(`${res.roleRequirementsUpdated} role req${res.roleRequirementsUpdated === 1 ? '' : 's'}`);
  if (res?.vacanciesUpdated) parts.push(`${res.vacanciesUpdated} vacanc${res.vacanciesUpdated === 1 ? 'y' : 'ies'}`);
  if (res?.workerPrimaryRoleUpdated) parts.push(`${res.workerPrimaryRoleUpdated} worker primary role${res.workerPrimaryRoleUpdated === 1 ? '' : 's'}`);
  if (res?.workerAdditionalRolesUpdated) parts.push(`${res.workerAdditionalRolesUpdated} worker addl roles`);
  return parts.length ? `Updated ${parts.join(', ')}` : '';
}
